/*
 * Sampo catalog UI: collection template "sampo" (header read-more, mobile quick filters and bottom sheets,
 * filter sidebar/drawer, selected-filter chips). The navigation (mega menu, mobile drawer) is in
 * assets/sampo-nav.js since 29.9.2026 (audit 28.9b M3).
 * Plain JavaScript, no dependencies. Loaded with defer by snippets/sampo-assets.liquid (possibly several
 * times per page, hence the guard).
 *
 * Filters and sorting are applied the simple, robust way: build the standard Shopify / Search & Discovery
 * URL (filter.p.m.*=value, sort_by=...) from the current URL and reload the page. Every other query
 * parameter is kept, including ?view=sampo while the template is being tested.
 */
(function () {
  'use strict';
  if (window.SampoCatalog) return;
  var SC = window.SampoCatalog = { version: '2026-09-30' };
  var doc = document;
  // Filter UI breakpoint: quick-filter bar + drawer below 1024 px (phones and tablets), sidebar from 1024 px
  // (audit 28.9b B5; was 767 px). Must match the 1023px / 1024px media queries in assets/sampo-catalog.css.
  var mqMobile = window.matchMedia('(max-width: 1023px)');
  var zeroState = false;

  function all(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function up(el, sel) { return el && el.closest ? el.closest(sel) : null; }
  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ------------------------------------------------------------------ URLs */
  function here() { return new URL(window.location.href); }

  // Back to the results after a filter / sort reload (UX-2, owner 30.9.2026): the reload used to land at the top.
  // When the visitor had scrolled down, the new URL gets #sampo-results (sections/sampo-active-filters.liquid, the
  // selected-filter row right above the grid) and the browser scrolls there itself, below the sticky header and bar
  // (scroll-margin-top in sampo-catalog.css). At the top of the page nothing changes. Back / forward keep the
  // browser's own scroll restoration: the hash only matters when a page is opened.
  var RESULTS = 'sampo-results';
  function scrolledDown() {
    return (window.pageYOffset || doc.documentElement.scrollTop || 0) > 120 && !!doc.getElementById(RESULTS);
  }
  function toResults(url) {
    url.hash = scrolledDown() ? RESULTS : '';
    return url;
  }

  function go(url) {
    url.searchParams.delete('page');
    toResults(url);
    var cur = here();
    if (url.pathname === cur.pathname && url.search === cur.search) {
      // nothing changed (e.g. "Näytä" with the same choices): no reload, which a hash-only change would not do anyway
      var t = url.hash ? doc.getElementById(RESULTS) : null;
      if (t && t.scrollIntoView) t.scrollIntoView();
      return;
    }
    doc.documentElement.classList.add('sampo-loading');
    window.location.assign(url.toString());
  }

  // filter.* params of the current URL as [name, value] pairs
  function filterPairs() {
    var out = [];
    here().searchParams.forEach(function (v, k) { if (k.indexOf('filter.') === 0) out.push([k, v]); });
    return out;
  }

  // Rebuild the filter params owned by `box` from its checked inputs; every other param stays.
  // Zero results: the drawer / sidebar owns every filter.* param, also those whose group is not shown (with 0
  // products Shopify reports no selected values, so such a group can disappear) -> it can always reset them.
  function compose(box) {
    var url = here();
    var inputs = all('input[data-sampo-fv]', box);
    var names = {};
    inputs.forEach(function (i) { names[i.name] = true; });
    if (zeroState && box && box.hasAttribute('data-sampo-drawer')) {
      filterPairs().forEach(function (kv) { names[kv[0]] = true; });
    }
    Object.keys(names).forEach(function (n) { url.searchParams.delete(n); });
    inputs.forEach(function (i) { if (i.checked) url.searchParams.append(i.name, i.value); });
    url.searchParams.delete('page');
    return url;
  }
  SC.compose = compose;

  /* ------------------------------------------------ "Näytä N tulosta" count */
  var countTimer = null;
  var countReq = null;

  function countSectionId() {
    var el = doc.querySelector('[data-sampo-active][data-section-id]');
    return el ? el.getAttribute('data-section-id') : '';
  }

  // n = null: count unknown. n = 0: the button is disabled, "Ei tuloksia – muuta valintoja" (audit 28.9b A1).
  function setCount(box, n) {
    var btn = box.querySelector('[data-sampo-apply]');
    if (!btn || !btn.hasAttribute('data-t-many')) return;
    var t;
    if (n === null) t = btn.getAttribute('data-t-any');
    else if (n === 0) t = btn.getAttribute('data-t-none');
    else if (n === 1) t = btn.getAttribute('data-t-one');
    else t = (btn.getAttribute('data-t-many') || '').replace('[n]', String(n));
    btn.disabled = (n === 0);
    btn.removeAttribute('aria-busy');
    if (t) btn.textContent = t;
    var live = box.querySelector('[data-sampo-live]');
    if (live && t) live.textContent = t;
  }

  function preview(box) {
    var btn = box.querySelector('[data-sampo-apply]');
    if (!btn) return;
    btn.setAttribute('aria-busy', 'true');
    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
      var sid = countSectionId();
      if (!sid || !window.fetch) { setCount(box, null); return; }
      var url = compose(box);
      url.searchParams.set('section_id', sid);
      if (countReq && countReq.abort) countReq.abort();
      countReq = window.AbortController ? new AbortController() : null;
      fetch(url.toString(), { credentials: 'same-origin', signal: countReq ? countReq.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) {
          var m = /data-sampo-count="(\d+)"/.exec(html);
          setCount(box, m ? parseInt(m[1], 10) : null);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          setCount(box, null);
        });
    }, 250);
  }

  /* ----------------------------------------------- dialogs (sheets, drawer) */
  var backdrop = null;
  var current = null;
  var opener = null;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function getBackdrop() {
    if (!backdrop) {
      backdrop = doc.createElement('div');
      backdrop.className = 'sampo-backdrop';
      backdrop.hidden = true;
      backdrop.addEventListener('click', function () { closeDialog(); });
      doc.body.appendChild(backdrop);
    }
    return backdrop;
  }

  function focusables(root) {
    return all(FOCUSABLE, root).filter(function (el) {
      return el.getClientRects().length > 0 && !up(el, '[hidden]');
    });
  }

  function openDialog(dlg, btn) {
    if (!dlg) return;
    if (current) closeDialog(true);
    current = dlg;
    opener = btn || null;
    all('input', dlg).forEach(function (i) { i.sampoWas = i.checked; });
    var apply = dlg.querySelector('[data-sampo-apply]');
    if (apply) { apply.sampoText = apply.textContent; apply.sampoDisabled = apply.disabled; }
    dlg.hidden = false;
    dlg.classList.add('is-open');
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    if (!dlg.hasAttribute('tabindex')) dlg.setAttribute('tabindex', '-1');
    getBackdrop().hidden = false;
    doc.body.style.overflow = 'hidden';
    if (opener) opener.setAttribute('aria-expanded', 'true');
    try { dlg.focus({ preventScroll: true }); } catch (e) { dlg.focus(); }
  }

  // keep = true: the choices are being applied, do not roll the inputs back.
  function closeDialog(silent, keep) {
    var dlg = current;
    if (!dlg) return;
    if (!keep) {
      all('input', dlg).forEach(function (i) { if (typeof i.sampoWas === 'boolean') i.checked = i.sampoWas; });
      var apply = dlg.querySelector('[data-sampo-apply]');
      if (apply && apply.sampoText) { apply.textContent = apply.sampoText; apply.removeAttribute('aria-busy'); }
      if (apply && typeof apply.sampoDisabled === 'boolean') apply.disabled = apply.sampoDisabled;
      clearTimeout(countTimer);
      if (countReq && countReq.abort) countReq.abort();
    }
    dlg.classList.remove('is-open');
    if (dlg.hasAttribute('data-sampo-sheet')) {
      dlg.hidden = true;
    } else {
      // the filter drawer is the desktop sidebar too: back to a plain region
      dlg.removeAttribute('role');
      dlg.removeAttribute('aria-modal');
    }
    getBackdrop().hidden = true;
    doc.body.style.overflow = '';
    var back = opener;
    if (back) back.setAttribute('aria-expanded', 'false');
    current = null;
    opener = null;
    if (!silent && back && back.focus) back.focus();
  }
  SC.open = openDialog;
  SC.close = closeDialog;

  doc.addEventListener('keydown', function (e) {
    if (!current) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeDialog();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables(current);
    if (!f.length) { e.preventDefault(); current.focus(); return; }
    var first = f[0];
    var last = f[f.length - 1];
    var a = doc.activeElement;
    if (!current.contains(a)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && (a === first || a === current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
  });

  /* ------------------------------------------------------------ clicks */
  doc.addEventListener('click', function (e) {
    var t = e.target;
    var el;

    if ((el = up(t, '[data-sampo-open]'))) {
      var dlg = doc.getElementById(el.getAttribute('data-sampo-open'));
      if (dlg) { e.preventDefault(); openDialog(dlg, el); }
      return;
    }

    if ((el = up(t, '[data-sampo-close]'))) {
      e.preventDefault();
      closeDialog();
      return;
    }

    if ((el = up(t, '[data-sampo-apply]'))) {
      var box = up(el, '[data-sampo-sheet], [data-sampo-drawer]');
      if (!box) return;
      e.preventDefault();
      var url;
      if (box.hasAttribute('data-sampo-sort')) {
        url = here();
        var r = box.querySelector('input[type="radio"]:checked');
        if (r) url.searchParams.set('sort_by', r.value);
      } else {
        url = compose(box);
      }
      closeDialog(true, true);
      go(url);
      return;
    }

    if ((el = up(t, '[data-sampo-clear]'))) {
      var cbox = up(el, '[data-sampo-sheet], [data-sampo-drawer]');
      if (!cbox) return;
      e.preventDefault();
      all('input[data-sampo-fv]', cbox).forEach(function (i) { i.checked = false; });
      preview(cbox);
      return;
    }

    if ((el = up(t, '[data-sampo-beginner]'))) {
      e.preventDefault();
      var param = el.getAttribute('data-param');
      var vals = (el.getAttribute('data-values') || '').split('|').filter(Boolean);
      if (!param || !vals.length) return;
      var on = el.getAttribute('aria-pressed') === 'true';
      if (!el.hasAttribute('data-sampo-was')) el.setAttribute('data-sampo-was', on ? 'true' : 'false');
      var bu = here();
      bu.searchParams.delete(param);
      if (!on) vals.forEach(function (v) { bu.searchParams.append(param, v); });
      el.setAttribute('aria-pressed', on ? 'false' : 'true');
      go(bu);
      return;
    }

    if ((el = up(t, '[data-sampo-more]'))) {
      var list = doc.getElementById(el.getAttribute('aria-controls'));
      if (!list) return;
      var expanded = !list.classList.toggle('is-collapsed');
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      el.textContent = expanded ? el.getAttribute('data-less') : el.getAttribute('data-more');
      return;
    }

    if ((el = up(t, '[data-sampo-tiles-more]'))) {
      // root pages' one-row tiles (UX-1): open / close the rest; opening moves focus to the first tile that appeared
      var tl = doc.getElementById(el.getAttribute('aria-controls'));
      if (!tl) return;
      var was = all('.sampo-tiles__item:not(.sampo-tiles__more)', tl).filter(function (li) { return !li.getClientRects().length; });
      var opened = tl.classList.toggle('is-open');
      el.setAttribute('aria-expanded', opened ? 'true' : 'false');
      var lab = el.querySelector('.sampo-tile__label');
      if (lab) lab.textContent = opened ? el.getAttribute('data-less') : el.getAttribute('data-more');
      var first = opened && was.length ? was[0].querySelector('a') : null;
      if (first) first.focus();
      return;
    }

    if ((el = up(t, '[data-sampo-readmore]'))) {
      var intro = doc.getElementById(el.getAttribute('aria-controls'));
      if (!intro) return;
      var open = intro.classList.toggle('is-open');
      el.setAttribute('aria-expanded', open ? 'true' : 'false');
      el.textContent = open ? el.getAttribute('data-less') : el.getAttribute('data-more');
      return;
    }

    if ((el = up(t, 'a[data-sampo-keep]'))) {
      // Server-built links (Shopify's url_to_remove) carry the sort_by of the page load, not a sort chosen
      // later with the theme's AJAX sort (booster.js replaceState): sort_by always follows the current URL.
      // view is only added when the link has none.
      try {
        var dest = new URL(el.getAttribute('href'), window.location.origin);
        var cur = here();
        var sort = cur.searchParams.get('sort_by');
        if (sort) dest.searchParams.set('sort_by', sort);
        else dest.searchParams.delete('sort_by');
        var view = cur.searchParams.get('view');
        if (view && !dest.searchParams.has('view')) dest.searchParams.set('view', view);
        dest.searchParams.delete('page');
        // (a link back to this very URL stays a plain reload: with the anchor it would only jump, not load)
        if (dest.pathname !== cur.pathname || dest.search !== cur.search) toResults(dest);
        el.setAttribute('href', dest.toString());
        // Ctrl / Cmd / Shift / Alt + click opens a new tab or window: this page stays, so no loading dim
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        doc.documentElement.classList.add('sampo-loading');
      } catch (err) { /* plain navigation */ }
    }
  });

  /* ----------------------------------------------------- filter changes */
  doc.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('input[data-sampo-fv]')) return;
    var sheet = up(t, '[data-sampo-sheet]');
    var drawer = up(t, '[data-sampo-drawer]');
    if (drawer && current !== drawer && !mqMobile.matches) {
      // desktop sidebar: apply at once, like today
      go(compose(drawer));
      return;
    }
    var box = sheet || drawer;
    if (box) preview(box);
  });

  /* ------------------------------------------------------------ layout */
  // Sticky quick-filter bar sits right under the theme's sticky header: the mobile header below 768 px, the
  // desktop header on tablets (768-1023 px, where the bar is shown since audit 28.9b B5).
  function syncTop() {
    var h = doc.querySelector('.page__header--mobile');
    if (!h || !h.getClientRects().length) h = doc.querySelector('.page__header--desktop') || h;
    var top = 0;
    if (h && h.getClientRects().length) {
      var sec = up(h, '.shopify-section');
      var pos = sec ? window.getComputedStyle(sec).position : '';
      if (pos === 'sticky' || pos === 'fixed' || window.getComputedStyle(h).position === 'fixed') {
        top = Math.round(h.getBoundingClientRect().height);
      }
    }
    doc.documentElement.style.setProperty('--sampo-top', top + 'px');
  }

  // The sticky quick-filter bar lives in the template wrapper, which also holds the SEO text and FAQ:
  // release it (static, scrolls away) once the product grid has scrolled past the line under the bar.
  // Observes the grid SECTION element: it survives booster.js re-rendering its content (sort, load more).
  var gridIO = null;
  function initGridStop() {
    if (gridIO) { gridIO.disconnect(); gridIO = null; }
    var bar = doc.querySelector('.sampo-sec--qbar');
    var mark = doc.querySelector('[data-sampo-grid]');
    var grid = mark ? (up(mark, '.shopify-section') || mark.parentNode) : null;
    if (!bar || !grid || !window.IntersectionObserver) return;
    var top = parseInt(window.getComputedStyle(doc.documentElement).getPropertyValue('--sampo-top'), 10) || 0;
    var cut = top + (bar.getBoundingClientRect().height || 60);
    gridIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var past = !en.isIntersecting && en.rootBounds && en.boundingClientRect.bottom <= en.rootBounds.top;
        bar.classList.toggle('is-past', !!past);
      });
    }, { rootMargin: '-' + Math.round(cut) + 'px 0px 0px 0px', threshold: 0 });
    gridIO.observe(grid);
  }

  // "Lue lisää" only when the 2-line clamp actually hides text.
  function initIntros() {
    all('[data-sampo-readmore]').forEach(function (btn) {
      var intro = doc.getElementById(btn.getAttribute('aria-controls'));
      if (!intro || intro.classList.contains('is-open')) return;
      btn.hidden = !(intro.scrollHeight > intro.clientHeight + 2);
    });
  }

  // Zero results (audit 28.9b A1): Shopify reports no selected values when the combination has no products, so
  // the server cannot show what is selected. sections/sampo-active-filters.liquid then marks itself
  // data-sampo-zero and carries every value's display name (<template data-sampo-fmap>); rebuild the state
  // from the URL: removable chips before "Tyhjennä kaikki", ticks in the drawer / sheets (enabled, so they can
  // be unticked), the filter buttons' selected look and the Suodata count.
  // Chip text for one selected filter.* value in the zero state. Shopify also leaves the selected value itself out
  // of filter.values then (checked 29.9.2026: Jättikoko + Pyöreä on a tiger page listed only Iso, Keskikoko, Neliö),
  // so a missing name falls back to the URL value, which is the value's own text for text metafields: sizes as
  // "40 × 50 cm", prices with ≥ / ≤; references (gid://) and yes/no values show the filter's label instead.
  function zeroChipName(names, labels, k, v) {
    if (names[k + '\n' + v]) return names[k + '\n' + v];
    if (names['*\n' + v]) return names['*\n' + v];  // fixed names (DP size classes), translated on .eu
    var range = k.match(/^(.*)\.(gte|lte)$/);
    if (range) return (labels[range[1]] ? labels[range[1]] + ' ' : '') + (range[2] === 'gte' ? '≥ ' : '≤ ') + v;
    var size = v.match(/^\s*(\d+)\s*[x×]\s*(\d+)\s*(cm)?\s*$/i);
    if (size) return size[1] + ' × ' + size[2] + ' cm';
    if (!v || v.indexOf('gid://') === 0 || /^(0|1|true|false)$/i.test(v)) return labels[k] || v;
    return v;
  }

  function initZero() {
    var box = doc.querySelector('[data-sampo-active][data-sampo-zero]');
    zeroState = !!box;
    if (!box || box.sampoZeroDone) return;
    box.sampoZeroDone = true;
    var list = box.querySelector('.sampo-active__list');
    var pairs = filterPairs();
    if (!pairs.length) {
      // no filters in the URL (products hidden for another reason): no clear controls that change nothing
      if (list) list.hidden = true;
      all('[data-sampo-zero-box] .sampo-zero__hint, [data-sampo-zero-box] .sampo-zero__clear').forEach(function (el) { el.hidden = true; });
      return;
    }
    var tpl = box.querySelector('template[data-sampo-fmap]');
    var clearItem = box.querySelector('[data-sampo-clear-item]');
    var names = {};
    var labels = {};
    var chipTpl = null;
    if (tpl && tpl.content) {
      chipTpl = tpl.content.querySelector('[data-sampo-chip-tpl]');
      all('[data-p]', tpl.content).forEach(function (s) {
        var p = s.getAttribute('data-p');
        if (s.hasAttribute('data-l')) labels[p] = s.textContent.trim();
        else names[p + '\n' + s.getAttribute('data-v')] = s.textContent.trim();
      });
    }
    var inputs = all('input[data-sampo-fv]');
    pairs.forEach(function (kv) {
      var k = kv[0];
      var v = kv[1];
      if (list && chipTpl) {
        var li = chipTpl.cloneNode(true);
        li.removeAttribute('data-sampo-chip-tpl');
        var u = here();
        var keep = u.searchParams.getAll(k).filter(function (x) { return x !== v; });
        u.searchParams.delete(k);
        keep.forEach(function (x) { u.searchParams.append(k, x); });
        u.searchParams.delete('page');
        var a = li.querySelector('a');
        if (a) a.setAttribute('href', u.pathname + u.search);
        var nm = li.querySelector('[data-sampo-chip-name]');
        if (nm) nm.textContent = zeroChipName(names, labels, k, v);
        list.insertBefore(li, clearItem);
      }
      inputs.forEach(function (i) {
        if (i.name !== k || i.value !== v) return;
        i.checked = true;
        i.defaultChecked = true;
        i.disabled = false;
        var sheet = up(i, '[data-sampo-sheet]');
        if (sheet && sheet.id) {
          all('[data-sampo-open]').forEach(function (b) {
            if (b.getAttribute('data-sampo-open') === sheet.id) b.classList.add('is-on');
          });
        }
      });
    });
    var main = doc.querySelector('.sampo-qb--main');
    if (main && !main.querySelector('.sampo-qb__badge')) {
      var badge = doc.createElement('span');
      badge.className = 'sampo-qb__badge';
      badge.textContent = String(pairs.length);
      var vh = doc.createElement('span');
      vh.className = 'sampo-vh';
      vh.textContent = ' ' + (main.getAttribute('data-t-selected') || '');
      badge.appendChild(vh);
      main.appendChild(badge);
    }
  }

  // Bottom sheets live in the sticky bar's section; move them to <body> so the sticky stacking context
  // cannot trap them under the header.
  function initSheets() {
    all('[data-sampo-sheet]').forEach(function (s) {
      if (s.parentNode !== doc.body) doc.body.appendChild(s);
    });
    var drawerBtn = doc.querySelector('[data-sampo-open="sampo-filter-drawer"]');
    if (drawerBtn && !doc.getElementById('sampo-filter-drawer')) drawerBtn.hidden = true;
  }

  /* -------------------------------------------------------------- init */
  function init() {
    syncTop();
    initIntros();
    initSheets();
    initZero();
    initGridStop();
  }

  // Back/forward cache: the page comes back exactly as it was left, i.e. with the choices that were just
  // being applied (ticks, aria-pressed, "Näytä N tulosta") while its URL and server markup are the old ones.
  // Put every control back to the state the server rendered.
  function restoreState() {
    doc.documentElement.classList.remove('sampo-loading');
    if (current) closeDialog(true);
    all('input[data-sampo-fv]').forEach(function (i) { i.checked = i.defaultChecked; });
    all('.sampo-sort input[type="radio"]').forEach(function (i) { i.checked = i.defaultChecked; });
    all('[data-sampo-beginner][data-sampo-was]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-sampo-was'));
      b.removeAttribute('data-sampo-was');
    });
    all('[data-sampo-apply]').forEach(function (b) {
      if (b.sampoText) b.textContent = b.sampoText;
      if (typeof b.sampoDisabled === 'boolean') b.disabled = b.sampoDisabled;
      b.removeAttribute('aria-busy');
    });
    all('[data-sampo-live]').forEach(function (l) { l.textContent = ''; });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', function () { syncTop(); initIntros(); initGridStop(); });
  window.addEventListener('resize', debounce(function () { syncTop(); initIntros(); initGridStop(); }, 150));
  window.addEventListener('pageshow', function (e) {
    doc.documentElement.classList.remove('sampo-loading');
    if (e.persisted) restoreState();
  });

  var onBreakpoint = function () { if (current) closeDialog(true); };
  if (mqMobile.addEventListener) mqMobile.addEventListener('change', onBreakpoint);
  else if (mqMobile.addListener) mqMobile.addListener(onBreakpoint);

  // Theme editor: a re-rendered section brings new sheets; drop the moved copies of the old one.
  doc.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (!id) return;
    if (current && current.getAttribute('data-sampo-owner') === id) closeDialog(true);
    all('[data-sampo-owner]').forEach(function (s) {
      if (s.getAttribute('data-sampo-owner') === id && s.parentNode === doc.body) s.parentNode.removeChild(s);
    });
  });
  doc.addEventListener('shopify:section:load', init);
})();
